import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, Group, IconRefresh, Loader, Token, Typography, Wrapper } from '@screentone/core';

import useSearchContent from 'components/search-content/hooks/useSearchContent';
import { useAllessehContentUpdateEvent } from 'contexts/allesseh-content-update/useAllessehContentUpdateEvent';
import { useAltSummOverrideContext } from 'contexts/altsumm-override/AltSummOverrideContext';
import { DataModelType } from 'contexts/datamodel/DataModelContext';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { useDragAndDrop } from 'contexts/drag-and-drop/useDragAndDrop';
import { AvailableFeatureFlags } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { reportError } from 'utils/errorBoundary';
import styles from './SearchByQueryTerm.module.scss';
import { SearchContentByProps } from '../search-by-url/SearchByUrl';
import SearchContentList from '../search-content-list/SearchContentList';

interface SearchByQueryTermParams extends SearchContentByProps {
  embargo?: boolean;
}

const SUBSCRIPTION_DEBOUNCE_MS = 1000;
const AUTO_REFRESH_DELAY_FALLBACK = 10; // seconds

const SearchByQueryTerm = ({
  searchParams,
  resultListClassName,
  renderContentCard,
  embargo,
  scratchpadItemIds
}: SearchByQueryTermParams) => {
  const { getAllArticleIds, type } = useDataModelContext();
  const { clearAllOverridesForContent } = useAltSummOverrideContext();
  const {
    data: searchData,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isLoading: isLoad,
    isFetching,
    fetchStatus,
    refetch
  } = useSearchContent(searchParams);
  const { isDraggingFromContentSearch } = useDragAndDrop();
  const autoRefreshEnabled = useFeatureFlag(AvailableFeatureFlags.AutorefreshContentSearchEnabled, false);
  const { data: publicationSettings } = usePublicationSettings();
  const refreshDelayMs =
    (publicationSettings?.publicationSetting.autoRefreshTimeout ?? AUTO_REFRESH_DELAY_FALLBACK) * 1000;

  // Keep a ref to the latest isDraggingFromContentSearch so the scheduled callback always reads the current value
  const isDraggingRef = useRef(isDraggingFromContentSearch);
  isDraggingRef.current = isDraggingFromContentSearch;

  // Track whether a subscription-driven refresh was skipped because a drag was in progress
  const pendingRefreshRef = useRef(false);

  const isLoading = isLoad && fetchStatus !== 'idle';

  const lastRefreshEvent = useAllessehContentUpdateEvent({
    enabled: autoRefreshEnabled,
    debounceMs: SUBSCRIPTION_DEBOUNCE_MS,
    delayMs: refreshDelayMs
  });

  const { pages } = searchData ?? {};
  const initContentItems = useMemo(
    () => (pages?.map((page) => page.data?.attributes).flat() ?? []) as AllessehContent[],
    [pages]
  );

  const excludedContentItemsInSearch = useMemo(
    () => initContentItems.filter((content) => searchParams.filters.excludedContentIds?.includes(content.data.id)),
    [initContentItems, searchParams.filters.excludedContentIds]
  );

  const includedContentItemsInSearch = useMemo(() => {
    const initContentItemsFiltered = initContentItems.filter((content) => {
      const filterByExcludedContentIds = !searchParams.filters.excludedContentIds?.includes(content.data.id);
      return filterByExcludedContentIds;
    });

    if (embargo) {
      return initContentItemsFiltered.filter(
        (content) =>
          content.data.attributes.embargo_datetime_utc &&
          new Date(content.data.attributes.embargo_datetime_utc).getTime() > Date.now()
      );
    }
    return initContentItemsFiltered;
  }, [initContentItems, searchParams.filters.excludedContentIds, embargo]);

  const refetchAndClearOverride = useCallback(() => {
    refetch()
      .then((result) => {
        const freshIds = (result.data?.pages.map((page) => page.data?.attributes).flat() ?? [])
          .filter((c): c is AllessehContent => c !== undefined)
          .map((c) => c.data.id);
        freshIds.forEach((id) => clearAllOverridesForContent(id));
      })
      .catch((err: unknown) => {
        reportError('[SearchByQueryTerm] Failed to refresh search results', err as Error);
      });
  }, [refetch, clearAllOverridesForContent]);

  const onClickRefresh = useCallback(() => {
    if (!isDraggingRef.current) {
      // Only refresh if not currently dragging from content search to prevent disrupting the user experience
      refetchAndClearOverride();
    } else {
      // Drag is in progress; schedule a refresh to fire once the drag ends
      pendingRefreshRef.current = true;
    }
  }, [refetchAndClearOverride]);

  // When drag ends, fire any refresh that was deferred while dragging
  useEffect(() => {
    if (!isDraggingFromContentSearch && pendingRefreshRef.current) {
      pendingRefreshRef.current = false;
      refetchAndClearOverride();
    }
  }, [isDraggingFromContentSearch, refetchAndClearOverride]);

  useEffect(() => {
    if (lastRefreshEvent) {
      onClickRefresh();
    }
  }, [lastRefreshEvent, onClickRefresh]);

  const nextPageProps = useMemo(
    () => ({
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage
    }),
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    if (!includedContentItemsInSearch.length && !nextPageProps.isFetchingNextPage && nextPageProps.hasNextPage) {
      // Load next page if all items on first page are excluded
      void fetchNextPage();
    }
  }, [fetchNextPage, includedContentItemsInSearch.length, nextPageProps.hasNextPage, nextPageProps.isFetchingNextPage]);

  const currentArticles = getAllArticleIds();

  const articlesAlreadyInUse = useMemo(
    () => initContentItems.filter((content) => currentArticles.includes(content.data.id)).length,
    [initContentItems, currentArticles]
  );

  const resultCount = searchData?.pages[0]
    ? (() => {
        const total = searchData.pages[0]?.links?.total;
        if (!total) return 0;

        const adjustedTotal = Math.max(0, total - articlesAlreadyInUse);
        return adjustedTotal > 100 ? '100+' : adjustedTotal;
      })()
    : 0;

  const isContentTypeFilterEmpty = searchParams.filters.contentTypes.length === 0;

  return (
    <>
      {!embargo && (
        <Wrapper className={styles.searchRefresh} data-testid="page-content-result">
          <Typography variant="label3" margin={{ vertical: 'xs' }} data-testid="page-result-label">
            Results
          </Typography>
          <Token color="gray" margin={{ right: 'sm' }} data-testid="page-result-count">
            {resultCount}
          </Token>
          <Group gap="xs">
            <Button
              size="sm"
              tertiary
              icon={IconRefresh}
              className={styles.searchRefreshButton}
              onClick={onClickRefresh}
            >
              Refresh
            </Button>
            {isFetching && <Loader size="md" />}
          </Group>
        </Wrapper>
      )}
      <SearchContentList
        className={resultListClassName}
        nextPageProps={nextPageProps}
        isLoading={isLoading}
        renderContentCard={renderContentCard}
        includedContentItemsInSearch={includedContentItemsInSearch}
        excludedContentItemsInSearch={excludedContentItemsInSearch}
        // Prevents min-height: 1000px on collections
        isContentTypeFilterEmpty={type === DataModelType.CollectionDTO || isContentTypeFilterEmpty}
        embargo={embargo}
        scratchpadItemIds={scratchpadItemIds}
      />
    </>
  );
};

export default SearchByQueryTerm;
